<!-- About (tab root, /tabs/about) — the concept note, BeanHub, the roasters,
     settings, help and the four documents. See IDEATION.md "6. About". -->
<template>
  <IonPage>
    <AppHeader title="About">
      <IonButtons slot="end">
        <IonButton @click="showHelp = true" aria-label="How to browse">
          <IonIcon :icon="helpCircleOutline" slot="icon-only" />
        </IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true">
      <AppLargeTitle title="About" />

      <div class="stack">
        <!-- The concept, on a photo of the shelving: the one place About
             shows the room the app is set in. -->
        <section class="concept" aria-labelledby="concept-title">
          <div class="concept__photo" aria-hidden="true" />
          <div class="concept__body">
            <p class="f-kicker">A concept for BeanHub</p>
            <h2 id="concept-title" class="f-display concept__title">Your favourite coffee shop, <em>every shelf of it.</em></h2>
            <p>
              BeanHub Shelf is an independent concept built as a pitch for
              BeanHub. It is not operated by, or affiliated with, BeanHub.
              Every coffee, roaster and price here is product data from
              <strong>beanhub.com.au</strong>, and the bags are the roasters’
              own photography. Checkout finishes on BeanHub's own site.
            </p>
          </div>
        </section>

        <!-- BeanHub links -->
        <IonList inset class="group">
          <IonListHeader>
            <IonLabel>BeanHub</IonLabel>
          </IonListHeader>
          <IonItem button :href="BEANHUB_URL" target="_blank" rel="noopener noreferrer">
            <IonIcon :icon="storefrontOutline" slot="start" aria-hidden="true" />
            <IonLabel>Shop on beanhub.com.au</IonLabel>
            <IonIcon :icon="openOutline" slot="end" aria-hidden="true" />
          </IonItem>
          <IonItem button :href="`${BEANHUB_URL}/pages/our-roasters`" target="_blank" rel="noopener noreferrer">
            <IonIcon :icon="peopleOutline" slot="start" aria-hidden="true" />
            <IonLabel>The roasters</IonLabel>
            <IonIcon :icon="openOutline" slot="end" aria-hidden="true" />
          </IonItem>
          <IonItem button :href="`${BEANHUB_URL}/pages/about-us`" target="_blank" rel="noopener noreferrer">
            <IonIcon :icon="bookOutline" slot="start" aria-hidden="true" />
            <IonLabel>BeanHub's story</IonLabel>
            <IonIcon :icon="openOutline" slot="end" aria-hidden="true" />
          </IonItem>
        </IonList>

        <!-- Roasters -->
        <IonList inset class="group">
          <IonListHeader>
            <IonLabel>
              <h2>Roasters</h2>
              <p>The independent roasters BeanHub stocks.</p>
            </IonLabel>
          </IonListHeader>
          <IonItem
            v-for="r in roasterRows"
            :key="r.slug"
            button
            :router-link="`/tabs/shelf/roaster/${r.slug}`"
            detail
          >
            <span class="swatch" :style="{ background: r.colour }" aria-hidden="true" />
            <IonLabel>
              <h3>{{ r.name }}</h3>
              <p>{{ r.count }} {{ r.count === 1 ? 'coffee' : 'coffees' }}</p>
            </IonLabel>
          </IonItem>
        </IonList>

        <!-- Settings -->
        <IonList inset class="group">
          <IonListHeader>
            <IonLabel>Settings</IonLabel>
          </IonListHeader>

          <IonItem>
            <IonLabel>View</IonLabel>
            <IonSegment slot="end" class="mini-segment" :value="settings.viewMode" @ionChange="settings.viewMode = $event.detail.value">
              <IonSegmentButton value="auto"><IonLabel>Auto</IonLabel></IonSegmentButton>
              <IonSegmentButton value="wall"><IonLabel>Wall</IonLabel></IonSegmentButton>
              <IonSegmentButton value="list"><IonLabel>List</IonLabel></IonSegmentButton>
            </IonSegment>
          </IonItem>

          <IonItem>
            <IonLabel>Motion</IonLabel>
            <IonSegment slot="end" class="mini-segment" :value="settings.motion" @ionChange="settings.motion = $event.detail.value">
              <IonSegmentButton value="system"><IonLabel>Sys</IonLabel></IonSegmentButton>
              <IonSegmentButton value="full"><IonLabel>Full</IonLabel></IonSegmentButton>
              <IonSegmentButton value="reduced"><IonLabel>Less</IonLabel></IonSegmentButton>
            </IonSegment>
          </IonItem>

          <IonItem button :detail="false" @click="refresh.run()" :disabled="refresh.busy.value">
            <IonIcon :icon="refreshOutline" slot="start" aria-hidden="true" :class="{ spinning: refresh.busy.value }" />
            <IonLabel>
              <h3>Catalogue</h3>
              <p v-if="refresh.busy.value">Refreshing…</p>
              <p v-else-if="refresh.error.value" class="field-error">{{ refresh.error.value }}</p>
              <p v-else>Updated {{ updatedRelative }} · Refresh</p>
            </IonLabel>
          </IonItem>

          <IonItem button :detail="false" @click="onClearBag" :disabled="bagCount === 0">
            <IonIcon :icon="trashOutline" slot="start" aria-hidden="true" color="danger" />
            <IonLabel>
              <h3>Clear bag</h3>
              <p>{{ bagCount === 0 ? 'Your bag is already empty.' : `Removes ${bagCount} ${bagCount === 1 ? 'item' : 'items'}, with undo.` }}</p>
            </IonLabel>
          </IonItem>
        </IonList>

        <!-- How to browse -->
        <IonList inset class="group">
          <IonItem button :detail="false" @click="showHelp = true">
            <IonIcon :icon="helpCircleOutline" slot="start" aria-hidden="true" />
            <IonLabel>How to browse</IonLabel>
          </IonItem>
        </IonList>

        <!-- The four documents -->
        <IonList inset class="group">
          <IonListHeader>
            <IonLabel>Legal</IonLabel>
          </IonListHeader>
          <IonItem button href="/privacy" detail>
            <IonIcon :icon="lockClosedOutline" slot="start" aria-hidden="true" />
            <IonLabel>Privacy policy</IonLabel>
          </IonItem>
          <IonItem button href="/terms" detail>
            <IonIcon :icon="documentTextOutline" slot="start" aria-hidden="true" />
            <IonLabel>Terms of service</IonLabel>
          </IonItem>
          <IonItem button href="/support" detail>
            <IonIcon :icon="mailOutline" slot="start" aria-hidden="true" />
            <IonLabel>Support</IonLabel>
          </IonItem>
          <IonItem button href="/about" detail>
            <IonIcon :icon="informationCircleOutline" slot="start" aria-hidden="true" />
            <IonLabel>About (web page)</IonLabel>
          </IonItem>
        </IonList>

        <p class="version">{{ APP_NAME }} · {{ versionLabel }}</p>
      </div>
    </IonContent>

    <!-- How to browse -->
    <IonModal :is-open="showHelp" :breakpoints="[0, 0.6, 1]" :initial-breakpoint="0.6" @didDismiss="showHelp = false">
      <IonContent class="ion-padding">
        <h2 class="f-display help-title">How to browse</h2>
        <IonList lines="none" class="help-list">
          <IonItem>
            <IonIcon :icon="handLeftOutline" slot="start" aria-hidden="true" />
            <IonLabel>
              <h3>Walk the shelves</h3>
              <p>Swipe along the wall; it settles on the nearest bay. The strip in the dock jumps straight to one.</p>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon :icon="swapHorizontalOutline" slot="start" aria-hidden="true" />
            <IonLabel>
              <h3>Re-rack it</h3>
              <p>Roaster, Flavour, Brew or Origin: every bag moves to its new shelf. Filters dim what doesn't match without moving anything.</p>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon :icon="bagHandleOutline" slot="start" aria-hidden="true" />
            <IonLabel>
              <h3>Pick one up</h3>
              <p>Tap a bag to lift it and read its label. Tap the bag in the sheet to turn it over and see the roaster's photos.</p>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon :icon="cubeOutline" slot="start" aria-hidden="true" />
            <IonLabel>
              <h3>See it in your room</h3>
              <p>Stand the bay you're looking at on your own floor, through the camera.</p>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon :icon="listOutline" slot="start" aria-hidden="true" />
            <IonLabel>
              <h3>List view</h3>
              <p>The toggle on Shelf, or View in Settings, switches to a plain list with everything the wall has.</p>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon :icon="keypadOutline" slot="start" aria-hidden="true" />
            <IonLabel>
              <h3>Keyboard</h3>
              <p>← / → walks between bays, Enter picks up the focused bag, Esc puts it back.</p>
            </IonLabel>
          </IonItem>
        </IonList>
        <button type="button" class="f-btn f-btn--block help-close" @click="showHelp = false">Got it</button>
      </IonContent>
    </IonModal>
  </IonPage>
</template>

<script setup>
import { computed, ref } from 'vue';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon,
  IonList, IonListHeader, IonItem, IonLabel,
  IonSegment, IonSegmentButton, IonModal,
} from '@ionic/vue';
import {
  helpCircleOutline, storefrontOutline, peopleOutline, bookOutline, openOutline,
  refreshOutline, trashOutline, lockClosedOutline, documentTextOutline, mailOutline,
  informationCircleOutline, handLeftOutline, swapHorizontalOutline, bagHandleOutline,
  cubeOutline, listOutline, keypadOutline,
} from 'ionicons/icons';
import { AppHeader, AppLargeTitle, useAction, removeWithUndo } from '../core/ui';
import { APP_NAME } from '../../app-config.js';
import { appVersion } from '../core/native.js';
import {
  roasters, settings, syncedAt, refreshCatalogue,
  bagCount, clearBag, roasterColour,
} from '../store.js';

const BEANHUB_URL = 'https://beanhub.com.au';

const showHelp = ref(false);

const roasterRows = computed(() =>
  roasters.value
    .map((r) => ({ ...r, colour: r.colour || roasterColour(r.slug) }))
    .sort((a, b) => (b.count || 0) - (a.count || 0)));

const refresh = useAction(async () => {
  const status = await refreshCatalogue();
  if (status === 'offline') throw new Error("Couldn't reach BeanHub. Showing the range from before.");
});

function onClearBag () {
  if (bagCount.value === 0) return;
  const n = bagCount.value;
  const revert = clearBag();
  removeWithUndo({ text: `Bag cleared (${n} ${n === 1 ? 'item' : 'items'}).`, commit: () => {}, revert });
}

function relativeTime (date) {
  const ms = Date.now() - date.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}
const updatedRelative = computed(() => relativeTime(syncedAt.value));

const pkgVersion = '1.0.0';
const versionLabel = ref(`v${pkgVersion}`);
appVersion().then((v) => { if (v) versionLabel.value = v }).catch(() => {});
</script>

<style scoped>
.stack {
  display: flex;
  flex-direction: column;
  /* One reading column on a wide screen, not a billboard. */
  max-width: 46rem;
  margin-inline: auto;
  gap: var(--sp-5);
  padding-bottom: var(--sp-7);
}

.concept {
  margin: 0 var(--gutter);
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--dock);
  box-shadow: var(--shadow-card);
}
.concept__photo {
  aspect-ratio: 16 / 7;
  background: var(--line, #ECE6DC) url('/wall/hero-wall.webp') 50% 40% / cover no-repeat;
}
.concept__body { padding: var(--sp-5) var(--sp-5) var(--sp-5); }
.concept__title {
  margin: var(--sp-2) 0 var(--sp-3);
  font-size: var(--text-2xl);
  letter-spacing: var(--display-tracking-xl);
}
.concept__title em { font-style: normal; color: var(--leaf); }
.concept p:last-child {
  margin: 0;
  color: var(--ink-2);
  line-height: 1.6;
}

.group ion-list-header p {
  color: var(--muted);
}

.swatch {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(31, 42, 34, 0.12);
  margin-right: var(--sp-3);
  flex: 0 0 auto;
}

.mini-segment {
  max-width: 210px;
  flex: 1 1 auto;
}
.mini-segment ion-segment-button {
  font-size: var(--text-xs, 0.75rem);
  min-width: 0;
  --padding-start: 4px;
  --padding-end: 4px;
}
.mini-segment ion-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
}

.spinning {
  animation: spin 900ms linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .spinning { animation: none; }
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.version {
  text-align: center;
  color: var(--muted);
  font: 500 0.6875rem / 1.4 var(--font-mono);
  margin: var(--sp-4) 0 0;
}

.help-title { margin: var(--sp-2) 0 var(--sp-3); font-size: var(--text-2xl); }
.help-close { margin-top: var(--sp-4); }
.help-list { background: transparent; }
.help-list h3 {
  margin: 0 0 2px;
}
.help-list p {
  color: var(--muted);
}
</style>
